import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { ROLE_RANK } from '@goss/types';
import { C } from '../lib/theme.js';
import { PageHeader, Card, Button, ErrorLine, EmptyState, Eyebrow } from '../ui/ui.js';
import { useToast } from '../ui/Toast.js';
import { apiFetch } from '../lib/api.js';
import { useOrg } from '../app/OrgContext.js';

/**
 * Statutory figures — the tenant-editable "facts" the Copy Engine quotes from DATA,
 * never from model memory (e.g. Propty: SDLT surcharge, CGT rate, Section 24). Each
 * carries an "as at" date; figures marked provisional are flagged for verification.
 * This is the guard against an ad ever quoting a stale statutory rate.
 */

interface Fact {
  id: string;
  key: string;
  label: string;
  value: string;
  note: string | null;
  as_at: string | null;
  source_url: string | null;
  is_future_deadline: boolean;
  active: boolean;
}

const labelStyle: CSSProperties = {
  display: 'grid',
  gap: 5,
  font: `500 10px 'IBM Plex Mono',monospace`,
  color: C.muted,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
};
const inputStyle: CSSProperties = {
  background: C.base,
  border: `1px solid ${C.line}`,
  borderRadius: 7,
  padding: '8px 10px',
  color: C.primary,
  font: `400 13px 'Inter',system-ui,sans-serif`,
};

export function StatutoryFigures() {
  const { orgId, me } = useOrg();
  const { toast } = useToast();
  const role = me?.memberships.find((m) => m.org?.id === orgId)?.role;
  const canEdit = role ? ROLE_RANK[role] >= ROLE_RANK.admin : false;

  const [facts, setFacts] = useState<Fact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const { facts } = await apiFetch<{ facts: Fact[] }>(`/api/orgs/${orgId}/facts`);
      setFacts(facts);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function seedUk() {
    if (!orgId) return;
    setBusy(true);
    try {
      const { inserted } = await apiFetch<{ inserted: number }>(
        `/api/orgs/${orgId}/facts/seed-uk-property`,
        { method: 'POST' },
      );
      toast(
        'success',
        inserted ? `Added ${inserted} UK property figures.` : 'All UK figures already present.',
      );
      await load();
    } catch (e) {
      toast('error', e instanceof Error ? e.message : 'Seed failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: '26px 30px 60px', maxWidth: 940 }}>
      <PageHeader
        eyebrow="Copy Engine · Compliance"
        title="Statutory figures"
        actions={
          canEdit ? (
            <Button variant="ghost" onClick={seedUk} disabled={busy}>
              Seed UK property figures
            </Button>
          ) : undefined
        }
      />

      <Card style={{ padding: '16px 18px', marginBottom: 18 }}>
        <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
          The Copy Engine quotes these values directly and is told never to use a figure from
          memory. Keep them current: a stale rate in an ad undermines the whole brand. Set an{' '}
          <strong style={{ color: C.primary }}>as-at date</strong> on each, and mark anything that
          is a moving or provisional deadline as{' '}
          <strong style={{ color: C.primary }}>provisional</strong> so generated copy that names a
          future year is flagged for a human check.
        </p>
      </Card>

      {error && <ErrorLine>{error}</ErrorLine>}

      {loading ? (
        <p style={{ color: C.muted, fontSize: 13 }}>Loading…</p>
      ) : facts.length === 0 ? (
        <EmptyState title="No statutory figures yet">
          {canEdit
            ? 'Add figures below, or seed the current UK property set.'
            : 'An admin can add the figures the Copy Engine should quote.'}
        </EmptyState>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {facts.map((f) => (
            <FactRow key={f.id} fact={f} orgId={orgId!} canEdit={canEdit} onChange={load} />
          ))}
        </div>
      )}

      {canEdit && <AddFact orgId={orgId!} onAdded={load} />}
    </div>
  );
}

function FactRow({
  fact,
  orgId,
  canEdit,
  onChange,
}: {
  fact: Fact;
  orgId: string;
  canEdit: boolean;
  onChange: () => Promise<void>;
}) {
  const { toast } = useToast();
  const [value, setValue] = useState(fact.value);
  const [note, setNote] = useState(fact.note ?? '');
  const [asAt, setAsAt] = useState(fact.as_at ?? '');
  const [future, setFuture] = useState(fact.is_future_deadline);
  const [source, setSource] = useState(fact.source_url ?? '');
  const [busy, setBusy] = useState(false);

  const dirty =
    value !== fact.value ||
    note !== (fact.note ?? '') ||
    asAt !== (fact.as_at ?? '') ||
    future !== fact.is_future_deadline ||
    source !== (fact.source_url ?? '');

  async function save() {
    setBusy(true);
    try {
      await apiFetch(`/api/orgs/${orgId}/facts/${fact.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          value,
          note,
          as_at: asAt || null,
          is_future_deadline: future,
          source_url: source || null,
        }),
      });
      toast('success', 'Saved.');
      await onChange();
    } catch (e) {
      toast('error', e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await apiFetch(`/api/orgs/${orgId}/facts/${fact.id}`, { method: 'DELETE' });
      await onChange();
    } catch (e) {
      toast('error', e instanceof Error ? e.message : 'Delete failed');
      setBusy(false);
    }
  }

  return (
    <Card style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>{fact.label}</div>
          <div style={{ font: `400 10.5px 'IBM Plex Mono',monospace`, color: C.faint }}>
            {fact.key}
          </div>
        </div>
        {future && (
          <span
            className="pill"
            style={{ color: C.watch, background: C.base, border: `1px solid ${C.line}` }}
          >
            provisional
          </span>
        )}
      </div>

      {canEdit ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12 }}>
            <label style={labelStyle}>
              Value
              <input style={inputStyle} value={value} onChange={(e) => setValue(e.target.value)} />
            </label>
            <label style={labelStyle}>
              As at
              <input
                type="date"
                style={inputStyle}
                value={asAt}
                onChange={(e) => setAsAt(e.target.value)}
              />
            </label>
          </div>
          <label style={labelStyle}>
            Note
            <input style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <label style={labelStyle}>
            Source URL
            <input style={inputStyle} value={source} onChange={(e) => setSource(e.target.value)} />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12.5,
                color: C.muted,
              }}
            >
              <input
                type="checkbox"
                checked={future}
                onChange={(e) => setFuture(e.target.checked)}
              />
              Provisional / future deadline (not settled law)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={remove} disabled={busy}>
                Delete
              </Button>
              <Button variant="primary" onClick={save} disabled={busy || !dirty}>
                Save
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13.5, color: C.primary }}>
          <strong>{fact.value}</strong>
          {fact.note ? <span style={{ color: C.muted }}> — {fact.note}</span> : null}
          {fact.as_at ? (
            <span style={{ color: C.faint, marginLeft: 8 }}>as at {fact.as_at}</span>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function AddFact({ orgId, onAdded }: { orgId: string; onAdded: () => Promise<void> }) {
  const { toast } = useToast();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!key.trim() || !label.trim() || !value.trim()) return;
    setBusy(true);
    try {
      await apiFetch(`/api/orgs/${orgId}/facts`, {
        method: 'POST',
        body: JSON.stringify({ key: key.trim(), label: label.trim(), value: value.trim() }),
      });
      setKey('');
      setLabel('');
      setValue('');
      toast('success', 'Added.');
      await onAdded();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={add} style={{ marginTop: 22 }}>
      <Eyebrow style={{ marginBottom: 10 }}>Add a figure</Eyebrow>
      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 140px auto', gap: 10 }}>
        <input
          style={inputStyle}
          placeholder="key (e.g. sdlt_surcharge)"
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <input
          style={inputStyle}
          placeholder="Label (human name)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <input
          style={inputStyle}
          placeholder="Value (e.g. 5%)"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button variant="primary" type="submit" disabled={busy}>
          Add
        </Button>
      </div>
    </form>
  );
}
